import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaBoxOpen, FaShoppingCart, FaClipboardList, FaExclamationTriangle, FaQrcode } from 'react-icons/fa';
import axios from 'axios';

const Dashboard = () => {
  const [niedrigerBestand, setNiedrigerBestand] = useState([]);
  const [warenkorbItems, setWarenkorbItems] = useState([]);
  const [offeneBestellungen, setOffeneBestellungen] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Parallel alle Daten abrufen
        const [artikelResponse, warenkorbResponse, bestellungenResponse] = await Promise.all([
          axios.get('/api/artikel'),
          axios.get('/api/warenkorb'),
          axios.get('/api/bestellungen')
        ]);
        
        // Artikel mit niedrigem Bestand filtern (weniger als 5 Einheiten)
        const niedrig = artikelResponse.data.filter(
          artikel => artikel.lagerbestand < 5
        );
        setNiedrigerBestand(niedrig);
        
        // Warenkorb setzen
        setWarenkorbItems(warenkorbResponse.data);
        
        // Offene Bestellungen filtern
        const offen = bestellungenResponse.data.filter(
          bestellung => ['ausstehend', 'bestellt'].includes(bestellung.status)
        );
        setOffeneBestellungen(offen);
      } catch (err) {
        console.error('Fehler beim Laden der Dashboard-Daten:', err);
        setError('Die Dashboard-Daten konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  return (
    <Container>
      <h1 className="mb-4">Dashboard</h1>
      
      {error && (
        <Alert variant="danger">{error}</Alert>
      )}
      
      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Laden...</span>
          </div>
          <p className="mt-2">Lade Dashboard-Daten...</p>
        </div>
      ) : (
        <>
          <Row className="mb-4">
            <Col lg={4} md={6} className="mb-3">
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">Artikel mit niedrigem Bestand</h5>
                    <FaExclamationTriangle className="text-warning" size={24} />
                  </div>
                  {niedrigerBestand.length === 0 ? (
                    <p className="text-success">Alle Artikel sind ausreichend vorhanden!</p>
                  ) : (
                    <div>
                      <p className="text-danger">{niedrigerBestand.length} Artikel unter Mindestbestand</p>
                      <ul className="ps-3">
                        {niedrigerBestand.slice(0, 3).map(artikel => (
                          <li key={artikel.id}>
                            <strong>{artikel.name}</strong>: nur {artikel.lagerbestand} Stk. auf Lager
                          </li>
                        ))}
                        {niedrigerBestand.length > 3 && <li>... und {niedrigerBestand.length - 3} weitere</li>}
                      </ul>
                    </div>
                  )}
                </Card.Body>
                <Card.Footer className="bg-white border-0">
                  <Button 
                    as={Link} 
                    to="/artikel" 
                    variant="outline-primary"
                    className="w-100"
                  >
                    <FaBoxOpen className="me-2" /> Artikel verwalten
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
            
            <Col lg={4} md={6} className="mb-3">
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">Warenkorb</h5>
                    <FaShoppingCart className="text-info" size={24} />
                  </div>
                  {warenkorbItems.length === 0 ? (
                    <p>Ihr Warenkorb ist leer.</p>
                  ) : (
                    <div>
                      <p>{warenkorbItems.length} Artikel im Warenkorb</p>
                      <ul className="ps-3">
                        {warenkorbItems.slice(0, 3).map(item => (
                          <li key={item.id}>
                            {item.menge}x {item.name}
                          </li>
                        ))}
                        {warenkorbItems.length > 3 && <li>... und {warenkorbItems.length - 3} weitere</li>}
                      </ul>
                    </div>
                  )}
                </Card.Body>
                <Card.Footer className="bg-white border-0">
                  <Button 
                    as={Link} 
                    to="/warenkorb" 
                    variant="outline-primary"
                    className="w-100"
                  >
                    <FaShoppingCart className="me-2" /> Zum Warenkorb
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
            
            <Col lg={4} md={6} className="mb-3">
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">Offene Bestellungen</h5>
                    <FaClipboardList className="text-success" size={24} />
                  </div>
                  {offeneBestellungen.length === 0 ? (
                    <p>Keine offenen Bestellungen.</p>
                  ) : (
                    <div>
                      <p>{offeneBestellungen.length} offene Bestellungen</p>
                      <ul className="ps-3">
                        {offeneBestellungen.slice(0, 3).map(bestellung => (
                          <li key={bestellung.id}>
                            <strong>{bestellung.bestellnummer}</strong>: {bestellung.status}
                          </li>
                        ))}
                        {offeneBestellungen.length > 3 && <li>... und {offeneBestellungen.length - 3} weitere</li>}
                      </ul>
                    </div>
                  )}
                </Card.Body>
                <Card.Footer className="bg-white border-0">
                  <Button 
                    as={Link} 
                    to="/bestellungen" 
                    variant="outline-primary"
                    className="w-100"
                  >
                    <FaClipboardList className="me-2" /> Alle Bestellungen
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col md={12}>
              <Card className="shadow-sm bg-primary text-white">
                <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-center p-4">
                  <div className="mb-3 mb-md-0">
                    <h4 className="mb-2">QR-Code Schnellzugriff</h4>
                    <p className="mb-0">Scannen Sie mit Ihrer externen QR-Code-Anwendung einen QR-Code, um schnell Artikel zu finden oder zum Warenkorb hinzuzufügen.</p>
                  </div>
                  <Button 
                    as="a" 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      alert('Bitte verwenden Sie Ihre externe QR-Scanner-App, um Artikel zu scannen.');
                    }}
                    variant="light"
                    className="px-4 py-2"
                  >
                    <FaQrcode className="me-2" /> QR-Code-Info
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default Dashboard;
